import React, { Fragment, useState, useEffect, useCallback } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTable } from '../../components/_EOS/Table'
import { WebhookSubscriber, WebhookEvent } from '../../interfaces/Webhook'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { ApiConfig } from '../../config/api'
import axios from 'axios'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { WebhookForm, WebhookFormEdit } from './form'
import { Status } from '../../hooks/useFetchState'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Chip, Typography } from '@mui/material'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/config'
import { ConfirmAction } from '../../components/ConfirmAction'

// --- API Helpers ---
const getWebhooks = (filters: Filter[], page = 0, pageSize = 5) => {
  const url = new URL(`${ApiConfig.WebhookApi}`)
  url.searchParams.set('page', page.toString())
  url.searchParams.set('pageSize', pageSize.toString())
  filters.forEach((filter) => {
    url.searchParams.append(filter.key, filter.value)
  })
  return axios.get(url.toString())
}

const deleteWebhook = (webhookSubscriberId: number) => {
  return axios.delete(`${ApiConfig.WebhookApi}/${webhookSubscriberId}`)
}

export const WebhookPage: React.FC = () => {
  const { state, callApi } = useApi<ApiResponse<WebhookSubscriber>>()
  const { state: deleteState, callApi: deleteCallApi } = useApi()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  // Modales
  const editModal = useModal<WebhookSubscriber>('Editar webhook')
  const createModal = useModal('Crear webhook')
  const confirmDeleteModal = useModal<number>('Confirmar eliminación') // Tipado para guardar el ID

  const pagination = usePagination(5)

  // Memorizamos la petición para evitar re-renders infinitos y facilitar su uso
  const requestWebhooks = useCallback(
    (
      filters: Filter[] = selectedFilters,
      page: number = pagination.page,
      pageSize: number = pagination.pageSize
    ) => {
      callApi(getWebhooks(filters, page, pageSize)).catch(console.error)
    },
    [selectedFilters, pagination.page, pagination.pageSize, callApi]
  )

  const requestDeleteWebhook = (id: number) => {
    deleteCallApi(deleteWebhook(id))
      .then(() => {
        openSnackBar('Webhook eliminado correctamente', true)
        confirmDeleteModal.closeModal()
        requestWebhooks()
      })
      .catch(() => openSnackBar('Error al eliminar el webhook', false))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    // IMPORTANTE: Aquí enviamos auxPage para que el usuario pueda navegar
    requestWebhooks(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  // Efecto para cambios en filtros: Reset total a página 0
  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    requestWebhooks(selectedFilters, 0, pagination.pageSize)
  }, [selectedFilters])

  // --- Render Helpers ---
  const setIconState = (active: boolean) => (
    <div
      style={{
        borderRadius: '20px',
        padding: '4px 12px',
        fontSize: '11px',
        backgroundColor: active ? '#1976d2' : '#f48fb1',
        color: active ? 'white' : 'black',
        textAlign: 'center',
        width: 'fit-content'
      }}
    >
      {active ? 'Activo' : 'Inactivo'}
    </div>
  )

  const generateWebhookEventsChips = (events: WebhookEvent[]) => {
    const totalEvents = Array.isArray(events) ? events.length : 0
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        {totalEvents > 0 ? (
          events
            .slice(0, 3)
            .map((event) => (
              <Chip
                key={event.webHookEventId}
                label={event.description}
                variant='outlined'
                size='small'
              />
            ))
        ) : (
          <span style={{ fontSize: '12px', color: '#999' }}>
            Sin notificación
          </span>
        )}
        {totalEvents > 3 && (
          <Chip label='...' variant='outlined' size='small' color='primary' />
        )}
      </div>
    )
  }

  return (
    <Fragment>
      <StandardPage
        pageTitle=''
      >
        <FilterComponent
          options={filterOptions}
          onSearchClick={() => {
            // Al buscar manualmente, reseteamos a página 0
            pagination.setPagination(0, pagination.pageSize)
            requestWebhooks(selectedFilters, 0, pagination.pageSize)
          }}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onAddButtonClick={() => createModal.openModal('md')}
          createButtonTooltip='Crear Webhook'
        />

        <GenericTable<WebhookSubscriber>
          headersConfig={[
            { field: 'webhookSubscriberId', title: 'ID' },
            { field: 'webHookUrl', title: 'URL' },
            {
              field: 'events',
              title: 'Tipo Notificación',
              formater: (value: unknown) =>
                generateWebhookEventsChips(value as WebhookEvent[])
            },
            {
              field: 'active',
              title: 'Estado',
              formater: (active: unknown) => setIconState(active as boolean)
            }
          ]}
          IDKey={'webhookSubscriberId'}
          data={state.data?.items}
          actions={{
            onEdit: (_, row) => editModal.openModal('md', row),
            onDelete: (id) => confirmDeleteModal.openModal('sm', id) // Abrimos modal con el ID
          }}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={
            state.status === Status.Loading ||
            deleteState.status === Status.Loading
          }
        />
      </StandardPage>

      {/* Modal Editar */}
      <Modal
        open={editModal.isOpen}
        onClose={editModal.closeModal}
        title={editModal.title}
        maxWidth={editModal.size}
      >
        {editModal.extendedProps && (
          <WebhookFormEdit
            webhookSubscriber={editModal.extendedProps}
            closeModal={(success) => {
              if (success) {
                editModal.closeModal()
                requestWebhooks()
              }
            }}
            handleSnackbar={openSnackBar}
          />
        )}
      </Modal>

      {/* Modal Crear */}
      <Modal
        open={createModal.isOpen}
        onClose={createModal.closeModal}
        title={createModal.title}
        maxWidth={createModal.size}
      >
        <WebhookForm
          onCreate={(success) => {
            if (success) {
              createModal.closeModal()
              requestWebhooks()
            }
          }}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      {/* Modal Confirmar Eliminación (Corregido) */}
      <Modal
        open={confirmDeleteModal.isOpen}
        onClose={confirmDeleteModal.closeModal}
        title={confirmDeleteModal.title}
        maxWidth='sm'
      >
        <ConfirmAction
          confirmTitle='¿Eliminar Webhook?'
          confirmText='Confirmar'
          negateText='Cancelar'
          handleSubmitAction={() =>
            requestDeleteWebhook(confirmDeleteModal.extendedProps!)
          }
          handleCloseModal={confirmDeleteModal.closeModal}
        >
          <Typography variant='body1' align='center'>
            ¿Está seguro que desea eliminar este suscriptor? Esta acción no se
            puede deshacer.
          </Typography>
        </ConfirmAction>
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
